import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import _debounce from 'lodash/debounce';
import cx from 'classnames';
import { openModal } from 'cards/actions/modalActions';
import RemoveIcon from 'assets/images/icons/remove.svg';
import { AppDispatch } from 'reducers/index';

import { ModalOptions } from 'components/common/types';
import styles from './styles.module.less';
import { ActiveFilters, FilterKey, FilterOptionList, FilterValue } from '../FilterTypes';
import { existenceCheck } from '../../utils';

const FilterMobileMenu = ({
  activeFilters,
  onUpdateMultipleSearchFilters,
  content: Component,
  optionList,
  clearValue,
  filterStateKey,
  filterList,
  showModal,
  title,
  hideModalFn,
}: FilterMobileMenuProps) => {
  const [windowInnerHeight, setWindowInnerHeight] = useState(window.innerHeight);
  const [localFilters, setLocalFilters] = useState<ActiveFilters>({
    ...activeFilters,
  });

  const noLocalFilterSelected =
    filterList.filter((filterKey: string) => {
      const typedFilterKey = filterKey as FilterKey;
      const currentValue = localFilters[typedFilterKey];
      // @ts-ignore
      return existenceCheck(currentValue);
    }).length < 1;

  useEffect(() => {
    setLocalFilters({
      ...activeFilters,
    });
  }, [activeFilters]);

  function updateLocalFilter(key: FilterKey, val: FilterValue) {
    setLocalFilters({
      ...localFilters,
      [key]: val,
    });
  }

  function applyFilter() {
    const newFilterValue: Record<string, unknown> = {};
    filterList.forEach((filterKey: string) => {
      const typedFilterKey = filterKey as FilterKey;
      if (filterKey === 'multiColor') {
        newFilterValue.seo_colors = localFilters.seo_colors;
      }
      newFilterValue[typedFilterKey] = localFilters[typedFilterKey];
    });
    // @ts-ignore
    onUpdateMultipleSearchFilters(newFilterValue);
    hideModalFn();
  }

  function clearLocalFilters() {
    setLocalFilters({
      ...localFilters,
      ...clearValue,
    });
  }

  useEffect(() => {
    const handleResize = _debounce(() => {
      setWindowInnerHeight(window.innerHeight);
    }, 100);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={styles.modalContainer} style={{ height: `${windowInnerHeight}px` }}>
      <div className={styles.modalHeader}>
        <ButtonV2
          className={cx(styles.clearBtn, {
            [styles.clearBtnDisabled]: noLocalFilterSelected,
          })}
          variant="secondary"
          onClick={clearLocalFilters}
          disabled={noLocalFilterSelected}
        >
          Clear
        </ButtonV2>
        <div className={styles.modalTitle}>{title}</div>
        <ButtonV2 className={styles.closeBtn} variant="secondary" onClick={hideModalFn}>
          <img src={RemoveIcon} width="20" height="20" alt="close" />
        </ButtonV2>
      </div>
      <div className={styles.modalDescription}>
        <Component
          optionList={optionList}
          activeFilters={localFilters}
          updateFilter={updateLocalFilter}
          activeValue={localFilters[filterStateKey]}
          activeSeoValue={filterStateKey === 'multiColor' ? localFilters.seo_colors : null}
          showModal={showModal}
        />
      </div>
      <div className={styles.modalAction}>
        <ButtonV2 className={styles.applyBtn} onClick={applyFilter}>
          Apply
        </ButtonV2>
      </div>
    </div>
  );
};

type keyValuePairsType = {
  [key in FilterKey]: FilterValue;
};

type FilterMobileMenuProps = {
  activeFilters: ActiveFilters;
  hideModalFn: () => void;
  onUpdateMultipleSearchFilters: (keyValuePairs: keyValuePairsType) => void;
  content: typeof React.Component;
  optionList: FilterOptionList;
  clearValue: Record<string, unknown>;
  filterStateKey: FilterKey;
  filterList: string[];
  title: string;
  showModal: (
    modalType: string,
    modalProps: Record<string, unknown>,
    modalOptions: ModalOptions
  ) => void;
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  showModal: (modalType: string, modalProps: Record<string, unknown>, modalOptions: ModalOptions) =>
    dispatch(openModal(modalType, modalProps, modalOptions)),
});

export default connect(null, mapDispatchToProps)(FilterMobileMenu);
